import { WORKFLOW_ANALYSIS_DAYS, WORKFLOW_ANALYSIS_DAYS_DEFAULT } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Progress, Select, Skeleton } from "@sagot/ui";
import { Fragment, useState } from "react";
import { useFormatters } from "../../shared/format";
import type { StepAnalysis } from "../p4-types";
import type { Definition, WorkflowDetail } from "../types";
import { usePathAnalysis } from "../use-workflows-p4";
import { answerRows, continuedRatio, outcomeRows, stepLabel } from "./analysis-model";

const A = "engage.workflows.analysis";
const COLUMNS = 7;
const PERCENT = 100;

function AnswerBreakdown({ step, definition }: { step: StepAnalysis; definition: Definition }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const rows = answerRows(step, definition, t(`${A}.startOver`));
  return (
    <tr className="bg-surface-2" data-testid="analysis-answers">
      <td colSpan={COLUMNS} className="px-4 py-3">
        <p className="mb-2 text-xs font-bold text-fg-muted">{t(`${A}.answersTitle`)}</p>
        <ul className="grid gap-2 sm:grid-cols-2">
          {rows.map((row) => (
            <li key={row.id} className="text-xs">
              <div className="flex justify-between gap-2">
                <span className="truncate">{row.label}</span>
                <span className="font-semibold tabular-nums">{t(`${A}.answerCount`, { count: row.count, share: format.percent(row.share) })}</span>
              </div>
              <Progress size="sm" value={row.share * PERCENT} label={row.label} valueText={format.percent(row.share)} className="mt-1" />
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

function OutcomeBreakdown({ step }: { step: StepAnalysis }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const rows = outcomeRows(step);
  return (
    <tr className="bg-surface-2" data-testid="analysis-outcomes">
      <td colSpan={COLUMNS} className="px-4 py-3">
        <p className="mb-2 text-xs font-bold text-fg-muted">{t(`${A}.outcomesTitle`)}</p>
        <ul className="flex flex-wrap gap-x-5 gap-y-1">
          {rows.map((row) => (
            <li key={row.id} className="flex items-baseline gap-1.5 text-xs">
              <span>{t(`${A}.outcomes.${row.id}`, { defaultValue: row.id })}</span>
              <span className="font-semibold tabular-nums">{format.number(row.count)}</span>
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

function StepRow({ step, definition }: { step: StepAnalysis; definition: Definition }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const ratio = continuedRatio(step);
  const typeName = t(`engage.workflows.steps.${step.type}`, { defaultValue: step.type });
  const label = stepLabel(step, typeName);
  return (
    <Fragment>
      <tr data-testid="analysis-row">
        <td className="max-w-72 truncate font-semibold">{label}</td>
        <td className="text-right tabular-nums">{format.number(step.reached)}</td>
        <td>
          {ratio === null ? (
            <span className="text-fg-subtle">—</span>
          ) : (
            <div className="flex items-center gap-2">
              <Progress size="sm" className="w-32" value={ratio * PERCENT} label={t(`${A}.continuedOf`, { step: label })} valueText={format.percent(ratio)} />
              <span className="text-xs tabular-nums">{format.percent(ratio)}</span>
            </div>
          )}
        </td>
        <td className="text-right tabular-nums">{format.number(step.dropped)}</td>
        <td className="text-right tabular-nums text-fg-muted">{format.number(step.in_progress)}</td>
        <td className={step.failed > 0 ? "text-right font-semibold tabular-nums text-warning" : "text-right tabular-nums"}>{format.number(step.failed)}</td>
        <td className="text-right tabular-nums">{step.filled === null ? <span className="text-fg-subtle">—</span> : format.number(step.filled)}</td>
      </tr>
      {step.answers && Object.keys(step.answers).length > 0 ? <AnswerBreakdown step={step} definition={definition} /> : null}
      {outcomeRows(step).length > 0 ? <OutcomeBreakdown step={step} /> : null}
    </Fragment>
  );
}

interface AnalysisPanelProps {
  workflow: WorkflowDetail;
  /** The definition the button names come from. */
  definition: Definition;
}

/** Where visitors continue and where they drop off, step by step, for one version and period. */
export function AnalysisPanel({ workflow, definition }: AnalysisPanelProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const [version, setVersion] = useState<number | null>(workflow.live_version);
  const [days, setDays] = useState<number>(WORKFLOW_ANALYSIS_DAYS_DEFAULT);
  const analysis = usePathAnalysis(workflow.id, version, days, true);
  const steps = analysis.data?.steps ?? [];
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{t(`${A}.title`)}</h2>
          <p className="text-sm text-fg-muted">{t(`${A}.subtitle`)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select aria-label={t(`${A}.version`)} className="h-10 w-auto" value={version === null ? "" : String(version)} onChange={(e) => { setVersion(e.target.value ? Number(e.target.value) : null); }}>
            <option value="">{t(`${A}.allVersions`)}</option>
            {workflow.versions.map((item) => <option key={item.id} value={String(item.number)}>{t(item.live ? `${A}.versionLive` : `${A}.versionName`, { number: item.number })}</option>)}
          </Select>
          <Select aria-label={t(`${A}.period`)} className="h-10 w-auto" value={String(days)} onChange={(e) => { setDays(Number(e.target.value)); }}>
            {WORKFLOW_ANALYSIS_DAYS.map((option) => <option key={option} value={String(option)}>{t(`${A}.days`, { count: option })}</option>)}
          </Select>
        </div>
      </div>
      {analysis.isError ? <ErrorState title={t(`${A}.loadFailed`)} onRetry={() => void analysis.refetch()} retryLabel={t("common.retry")} /> : null}
      {analysis.isPending ? <Skeleton className="h-64 rounded-card" aria-hidden="true" /> : null}
      {analysis.data && steps.length === 0 ? <EmptyState icon="bar-chart" headingLevel={3} title={t(`${A}.emptyTitle`)} description={t(`${A}.emptyBody`)} /> : null}
      {analysis.data && steps.length > 0 ? (
        <>
          <p className="text-sm text-fg-muted" data-testid="analysis-sent">{t(`${A}.sent`, { count: analysis.data.sent, formatted: format.number(analysis.data.sent) })}</p>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable) */}
          <div className="card overflow-x-auto focus-visible:outline-2 focus-visible:outline-primary" role="region" aria-label={t(`${A}.title`)} tabIndex={0}>
            <table className="table">
              <caption className="sr-only">{t(`${A}.title`)}</caption>
              <thead>
                <tr>
                  <th scope="col">{t(`${A}.columns.step`)}</th>
                  <th scope="col" className="text-right">{t(`${A}.columns.reached`)}</th>
                  <th scope="col">{t(`${A}.columns.continued`)}</th>
                  <th scope="col" className="text-right">{t(`${A}.columns.dropped`)}</th>
                  <th scope="col" className="text-right">{t(`${A}.columns.waiting`)}</th>
                  <th scope="col" className="text-right">{t(`${A}.columns.failed`)}</th>
                  <th scope="col" className="text-right">{t(`${A}.columns.filled`)}</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((step) => <StepRow key={`${step.path}-${step.step_id}`} step={step} definition={definition} />)}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-fg-subtle">{t(`${A}.note`)}</p>
        </>
      ) : null}
    </div>
  );
}
